import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  Typography,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  RadioGroup,
  Radio,
  OutlinedInput,
  SelectChangeEvent
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import apiService from '../../services/api';
import {
  ScheduledAnalysis,
  ScheduledAnalysisCreate,
  ScheduleType,
  DateRangeType
} from '../../types/scheduled';
import { AnalysisType } from '../../types';
import { useCourseLabels } from '../../hooks/useCourseLabels';
import { useParticipantLabels } from '../../hooks/useParticipantLabels';
import { useSettingsStore } from '../../stores';
import {
  getPreferredTimeZone,
  localDateToWallClockTime,
  wallClockTimeToLocalDate,
} from '../../utils/time';

interface ScheduleDialogProps {
  open: boolean;
  schedule?: ScheduledAnalysis | null;
  onClose: () => void;
  onSuccess: () => void;
}

const WEEKDAYS = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' }
];

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({ open, schedule, onClose, onSuccess }) => {
  const { courseSingular, allLabel } = useCourseLabels();
  const { singular, plural, pluralLower, singularLower } = useParticipantLabels();
  const preferredTimezone = useSettingsStore((state) => state.settings.temporal.preferredTimezone);
  const [availableCourses, setAvailableCourses] = useState<{ id: string; name: string }[]>([]);
  const [coursesData, setCoursesData] = useState<Record<string, string[]>>({});  // Store course -> students mapping
  const [availableStudents, setAvailableStudents] = useState<string[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    course_id: '',
    students: [] as string[],
    device_type: 'all',
    analysis_type: AnalysisType.MULTI_DEVICE,
    schedule_type: ScheduleType.DAILY,
    schedule_time: new Date(),
    schedule_days: [] as string[],
    date_range_type: DateRangeType.LAST_7_DAYS,
    enable_ai_insights: true,
    processing_mode: 'single' as 'single' | 'individual'  // single report or individual per entity
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load courses on mount
  useEffect(() => {
    if (open) {
      apiService.getSentinelCourses().then((courses: Record<string, string[]>) => {
        // Store the full courses data (course_id -> students array)
        setCoursesData(courses);
        
        // Create course list for dropdown
        const courseList = Object.entries(courses).map(([id, _students]) => ({
          id,
          name: id
        }));
        setAvailableCourses(courseList);
      });
    }
  }, [open]);

  // Load participants when course changes (from cached course data)
  useEffect(() => {
    if (open && formData.course_id && coursesData[formData.course_id]) {
      setLoadingStudents(true);
      // Get students from the already-loaded course data
      const students = coursesData[formData.course_id];
      setAvailableStudents(students || []);
      setLoadingStudents(false);
    } else if (!formData.course_id) {
      setAvailableStudents([]);
    }
  }, [formData.course_id, open, coursesData]);

  // Load schedule data when editing
  useEffect(() => {
    if (schedule) {
      const localTime = wallClockTimeToLocalDate(schedule.schedule_time);

      setFormData({
        name: schedule.name,
        description: schedule.description || '',
        course_id: schedule.analysis_config.course_id || '',
        students: schedule.analysis_config.students || [],
        device_type: schedule.analysis_config.device_type,
        analysis_type: schedule.analysis_config.analysis_type as AnalysisType,
        schedule_type: schedule.schedule_type,
        schedule_time: localTime,
        schedule_days: schedule.schedule_days?.split(',') || [],
        date_range_type: schedule.date_range_type,
        enable_ai_insights: schedule.analysis_config.parameters?.enable_ai_insights || false,
        processing_mode: schedule.analysis_config.processing_mode || 'single'
      });
    } else {
      // Reset for new schedule
      const now = new Date();
      now.setHours(9, 0, 0);  // Default to 9:00 AM
      setFormData({
        name: '',
        description: '',
        course_id: '',
        students: [],
        device_type: 'all',
        analysis_type: AnalysisType.MULTI_DEVICE,
        schedule_type: ScheduleType.DAILY,
        schedule_time: now,
        schedule_days: [],
        date_range_type: DateRangeType.LAST_7_DAYS,
        enable_ai_insights: true,
        processing_mode: 'single'
      });
    }
    setErrors({});
  }, [schedule, open]);

  const createMutation = useMutation({
    mutationFn: (data: ScheduledAnalysisCreate) => apiService.createScheduledAnalysis(data),
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      setErrors({ submit: error.response?.data?.detail || 'Failed to create schedule' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      apiService.updateScheduledAnalysis(id, data),
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      console.error('Update error:', error.response?.data);
      const errorMsg = error.response?.data?.detail || JSON.stringify(error.response?.data) || 'Failed to update schedule';
      setErrors({ submit: errorMsg });
    }
  });

  const handleSubmit = () => {
    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.schedule_type === ScheduleType.WEEKLY && formData.schedule_days.length === 0) {
      newErrors.schedule_days = 'Select at least one day';
    }
    if (formData.processing_mode === 'individual' && formData.students.length === 0) {
      newErrors.students = `Individual processing requires at least one ${singularLower} to be selected`;
    }
    if (formData.processing_mode === 'individual' && formData.students.length > 10) {
      newErrors.students = `Individual processing limited to 10 ${pluralLower} maximum`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const timeString = localDateToWallClockTime(formData.schedule_time);
    const scheduleTimezone = schedule?.timezone || getPreferredTimeZone(preferredTimezone);

    // Build analysis config
    const analysisConfig = {
      students: formData.students,
      course_id: formData.course_id || undefined,
      device_type: formData.device_type,
      analysis_type: formData.analysis_type,
      processing_mode: formData.processing_mode,
      parameters: {
        enable_dwell_detection: true,
        enable_dwell_based_routes: true,
        enable_non_dwell_routes: true,
        enable_temporal_analysis: true,
        enable_connectivity_analysis: true,
        enable_route_consistency: true,
        enable_place_lookup: true,
        enable_ai_insights: formData.enable_ai_insights,
        dwell_radius: 50.0,
        stay_point_time_threshold: 300,
        stay_point_distance_threshold: 50.0,
        min_route_length: 5,
        min_segment_distance: 100.0,
        route_similarity_threshold: 50.0,
        max_dwell_locations: 100,
        max_routes: 100
      }
    };

    const scheduleData: any = {
      name: formData.name,
      description: formData.description || undefined,
      analysis_config: analysisConfig,
      schedule_type: formData.schedule_type,
      schedule_time: timeString,
      schedule_days: formData.schedule_type === ScheduleType.WEEKLY 
        ? formData.schedule_days.join(',') 
        : undefined,
      timezone: scheduleTimezone,
      date_range_type: formData.date_range_type,
      is_active: true
    };

    // Remove undefined values for update to avoid validation errors
    if (schedule) {
      const cleanData = Object.fromEntries(
        Object.entries(scheduleData).filter(([_, v]) => v !== undefined)
      );
      updateMutation.mutate({ id: schedule.id, data: cleanData });
    } else {
      createMutation.mutate(scheduleData);
    }
  };

  const handleDayToggle = (day: string) => {
    setFormData(prev => ({
      ...prev,
      schedule_days: prev.schedule_days.includes(day)
        ? prev.schedule_days.filter(d => d !== day)
        : [...prev.schedule_days, day]
    }));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const scheduleTimezone = schedule?.timezone || getPreferredTimeZone(preferredTimezone);
  const selectedDeviceType = formData.device_type || 'all';
  const hasSpecificDeviceType = selectedDeviceType.toLowerCase() !== 'all';
  const deviceRequirementLabel = hasSpecificDeviceType && selectedDeviceType.toLowerCase() === 'vehicle'
    ? 'vehicle-mounted GPS device'
    : `${selectedDeviceType.toLowerCase()} GPS device`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {schedule ? 'Edit Scheduled Analysis' : 'Create Scheduled Analysis'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Basic Info */}
          <TextField
            label="Schedule Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            error={!!errors.name}
            helperText={errors.name || `e.g., "Weekly ${singular}-07 Analysis"`}
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            multiline
            rows={2}
            fullWidth
          />

          {/* Analysis Configuration */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
            Analysis Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{courseSingular}</InputLabel>
                <Select
                  value={formData.course_id}
                  onChange={(e) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      course_id: e.target.value,
                      students: []  // Reset students when course changes
                    }));
                  }}
                  label={courseSingular}
                >
                  <MenuItem value="">{allLabel}</MenuItem>
                  {availableCourses.map((course) => (
                    <MenuItem key={course.id} value={course.id}>
                      {course.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{plural}</InputLabel>
                <Select
                  multiple
                  value={formData.students}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    const value = e.target.value;
                    setFormData(prev => ({ 
                      ...prev, 
                      students: typeof value === 'string' ? value.split(',') : value
                    }));
                  }}
                  input={<OutlinedInput label={plural} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  disabled={loadingStudents || !formData.course_id}
                >
                  {loadingStudents ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {`Loading ${pluralLower}...`}
                    </MenuItem>
                  ) : (
                    availableStudents.map((student) => (
                      <MenuItem key={student} value={student}>
                        {student}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Processing Mode
                </Typography>
                <RadioGroup
                  row
                  value={formData.processing_mode}
                  onChange={(e) => setFormData(prev => ({ ...prev, processing_mode: e.target.value as 'single' | 'individual' }))}
                >
                  <FormControlLabel 
                    value="single" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body2">Single Combined Report</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`All selected ${pluralLower} in one analysis`}
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="individual" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body2">{`Individual Reports (Max 10 ${pluralLower})`}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`Separate analysis for each ${singularLower}`}
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
                {formData.processing_mode === 'individual' && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      <strong>Warning:</strong> Individual mode creates {formData.students.length || 'N'} separate jobs. 
                      {` Select specific ${pluralLower} (max 10) to avoid overwhelming the system.`}
                    </Typography>
                  </Alert>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Analysis Type</InputLabel>
                <Select
                  value={formData.analysis_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, analysis_type: e.target.value as AnalysisType }))}
                  label="Analysis Type"
                >
                  <MenuItem value={AnalysisType.MULTI_DEVICE}>Multi-Device</MenuItem>
                  <MenuItem value={AnalysisType.FULL}>Recent Activity Analysis</MenuItem>
                  <MenuItem value={AnalysisType.QUICK}>Quick Analysis</MenuItem>
                  <MenuItem value={AnalysisType.ROUTE_ONLY}>Route Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={formData.date_range_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_range_type: e.target.value as DateRangeType }))}
                  label="Date Range"
                >
                  <MenuItem value={DateRangeType.LAST_3_DAYS}>Last 3 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_7_DAYS}>Last 7 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_14_DAYS}>Last 14 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_30_DAYS}>Last 30 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_60_DAYS}>Last 60 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_90_DAYS}>Last 90 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Alert severity={hasSpecificDeviceType ? 'warning' : 'info'}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Data requirement
            </Typography>
            <Typography variant="body2">
              {hasSpecificDeviceType
                ? `This schedule is limited to ${selectedDeviceType} data. Each selected ${singularLower} must have a ${deviceRequirementLabel} assigned and reporting during the selected date range, or the run will show no data for selection.`
                : `Scheduled analyses require GPS data for the selected ${pluralLower} during the selected date range. If a schedule is limited to one device type, the selected ${singularLower} must have that matching device assigned and reporting.`}
            </Typography>
          </Alert>

          {/* Schedule Configuration */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
            Schedule Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.schedule_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, schedule_type: e.target.value as ScheduleType }))}
                  label="Frequency"
                >
                  <MenuItem value={ScheduleType.ONCE}>Once</MenuItem>
                  <MenuItem value={ScheduleType.DAILY}>Daily</MenuItem>
                  <MenuItem value={ScheduleType.WEEKLY}>Weekly</MenuItem>
                  <MenuItem value={ScheduleType.MONTHLY}>Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Time (Local)"
                type="time"
                value={formData.schedule_time.toTimeString().substring(0, 5)}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':');
                  const newTime = new Date();
                  newTime.setHours(parseInt(hours), parseInt(minutes), 0);
                  setFormData(prev => ({ ...prev, schedule_time: newTime }));
                }}
                fullWidth
                helperText={`Wall-clock time in ${scheduleTimezone}`}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Weekly Days Selection */}
            {formData.schedule_type === ScheduleType.WEEKLY && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Select Days
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {WEEKDAYS.map(({ value, label }) => (
                    <Chip
                      key={value}
                      label={label}
                      onClick={() => handleDayToggle(value)}
                      color={formData.schedule_days.includes(value) ? 'primary' : 'default'}
                      variant={formData.schedule_days.includes(value) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
                {errors.schedule_days && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.schedule_days}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>

          {/* Options */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.enable_ai_insights}
                onChange={(e) => setFormData(prev => ({ ...prev, enable_ai_insights: e.target.checked }))}
              />
            }
            label="Enable AI Insights"
          />

          {/* Error Display */}
          {errors.submit && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {errors.submit}
              </Typography>
            </Alert>
          )}
          {errors.students && (
            <Alert severity="error">{errors.students}</Alert>
          )}

          {/* Summary Preview */}
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Schedule Summary:
            </Typography>
            <Typography variant="body2">
              <strong>Frequency:</strong> {formData.schedule_type === ScheduleType.WEEKLY && formData.schedule_days.length > 0 
                ? `Every ${formData.schedule_days.join(', ')}`
                : formData.schedule_type === ScheduleType.DAILY
                ? 'Every day'
                : formData.schedule_type === ScheduleType.MONTHLY
                ? 'Every month'
                : 'Once'
              } at {formData.schedule_time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} ({scheduleTimezone})
              <br />
              <strong>{courseSingular}:</strong> {formData.course_id || allLabel}
              <br />
              <strong>{plural}:</strong> {formData.students.length > 0 ? formData.students.join(', ') : `All ${pluralLower}`}
              <br />
              <strong>Date Range:</strong> {formData.date_range_type.replace(/_/g, ' ')}
              <br />
              <strong>Device Data:</strong> {hasSpecificDeviceType ? selectedDeviceType : 'Any available device type'}
              <br />
              <strong>Processing:</strong> {formData.processing_mode === 'individual' 
                ? `${formData.students.length || 'All'} separate reports` 
                : '1 combined report'}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending}
        >
          {isPending ? <CircularProgress size={20} /> : (schedule ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;

